import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminPromoCodesAPI, adminTenantSettingsAPI, adminEventTournamentsAPI } from '../services/adminApi';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  max-width: 960px;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

const FormSection = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
`;

const SectionHeading = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  &.fullWidth { grid-column: 1 / -1; }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
`;

const PrimaryButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) { background: #2563eb; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  &:hover { background: #e5e7eb; }
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  background-color: ${p => p.$success ? '#d1fae5' : '#fee2e2'};
  color: ${p => p.$success ? '#065f46' : '#991b1b'};
  border: 1px solid ${p => p.$success ? '#a7f3d0' : '#fecaca'};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
  }
  tbody tr:hover {
    background: #f9fafb;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${p => p.$active ? '#d1fae5' : '#fee2e2'};
  color: ${p => p.$active ? '#065f46' : '#991b1b'};
`;

const ToggleBtn = styled.button`
  background: ${p => p.$active ? '#ef4444' : '#10b981'};
  color: white;
  border: none;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const EditBtn = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  margin-left: 0.5rem;
  &:hover:not(:disabled) { background: #2563eb; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const EmptyState = styled.p`
  padding: 1.5rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const LoadingState = styled.p`
  padding: 1.5rem;
  text-align: center;
  color: #6b7280;
`;

const ToggleSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;
const ToggleLabel = styled.div`
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
`;
const ToggleHint = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

function toLocalDatetimeInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch (_) {
    return String(value);
  }
}

export default function AdminSettingsPromoCreate() {
  const [message, setMessage] = useState({ text: '', success: false });
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    code: '',
    description: '',
    campaign_id: '',
    season_id: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    validUntil: '',
    is_active: true,
  });
  const [editForm, setEditForm] = useState({
    description: '',
    campaign_id: '',
    season_id: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    validUntil: '',
    is_active: true,
  });

  const queryClient = useQueryClient();
  const { data: listData, isLoading } = useQuery('admin-promo-codes', () => adminPromoCodesAPI.getAll(), { retry: false });
  const { data: campaignsData } = useQuery(
    'admin-promo-campaigns',
    () => adminEventTournamentsAPI.getAll(),
    { retry: false }
  );
  const campaigns = Array.isArray(campaignsData?.data)
    ? campaignsData.data
    : Array.isArray(campaignsData)
      ? campaignsData
      : [];

  const createCampaignId = form.campaign_id;
  const { data: linkedSeasonsData, isLoading: linkedSeasonsLoading } = useQuery(
    ['admin-promo-linked-seasons', createCampaignId],
    () => adminEventTournamentsAPI.getLinkedSeasons(createCampaignId),
    { enabled: !!createCampaignId, retry: false }
  );
  const linkedSeasonsRaw = linkedSeasonsData?.data ?? linkedSeasonsData ?? [];
  const linkedSeasons = (Array.isArray(linkedSeasonsRaw) ? linkedSeasonsRaw : []).map((s) => ({
    id: s.season_id || s.id,
    name: s.season_name || s.name,
    status: s.season_status || s.status,
  }));

  const editCampaignId = editForm.campaign_id;
  const { data: editLinkedData } = useQuery(
    ['admin-promo-linked-seasons-edit', editCampaignId],
    () => adminEventTournamentsAPI.getLinkedSeasons(editCampaignId),
    { enabled: !!editCampaignId && !!editingId, retry: false }
  );
  const editLinkedRaw = editLinkedData?.data ?? editLinkedData ?? [];
  const editLinkedSeasons = (Array.isArray(editLinkedRaw) ? editLinkedRaw : []).map((s) => ({
    id: s.season_id || s.id,
    name: s.season_name || s.name,
    status: s.season_status || s.status,
  }));

  const { data: tenantData } = useQuery('admin-settings-tenant', () => adminTenantSettingsAPI.get(), { retry: false });
  const promoCodes = listData?.data?.promoCodes ?? [];
  const allowTeamReferral = tenantData?.data?.allow_team_referral_codes !== false;
  const teamRefDiscountType = tenantData?.data?.team_referral_discount_type || 'percentage';
  const serverTeamRefValue = tenantData?.data?.team_referral_discount_value ?? 20;
  const loyaltyDiscountType =
    tenantData?.data?.loyalty_discount_type
    || tenantData?.data?.team_referral_discount_type
    || 'percentage';
  const serverLoyaltyValue =
    tenantData?.data?.loyalty_discount_value
    ?? tenantData?.data?.team_referral_discount_value
    ?? 20;
  const [localTeamRefValue, setLocalTeamRefValue] = useState(serverTeamRefValue);
  const [localLoyaltyValue, setLocalLoyaltyValue] = useState(serverLoyaltyValue);
  useEffect(() => {
    setLocalTeamRefValue(serverTeamRefValue);
  }, [serverTeamRefValue]);
  useEffect(() => {
    setLocalLoyaltyValue(serverLoyaltyValue);
  }, [serverLoyaltyValue]);

  const tenantSettingsMutation = useMutation(
    (data) => adminTenantSettingsAPI.update(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-settings-tenant');
      },
    }
  );

  const createMutation = useMutation(
    (data) => adminPromoCodesAPI.create(data),
    {
      onSuccess: (res) => {
        const code = res?.data?.promoCode?.code || 'Promo code';
        setMessage({ text: `Promo code "${code}" has been created successfully.`, success: true });
        setForm({
          code: '',
          description: '',
          campaign_id: '',
          season_id: '',
          discountType: 'percentage',
          discountValue: '',
          maxUses: '',
          validUntil: '',
          is_active: true,
        });
        queryClient.invalidateQueries('admin-promo-codes');
      },
      onError: (e) => {
        setMessage({ text: e.response?.data?.message || 'Failed to create promo code.', success: false });
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => adminPromoCodesAPI.update(id, data),
    {
      onSuccess: () => {
        setMessage({ text: 'Promo code updated.', success: true });
        setEditingId(null);
        queryClient.invalidateQueries('admin-promo-codes');
      },
      onError: (e) => {
        setMessage({ text: e.response?.data?.message || 'Failed to update.', success: false });
      },
    }
  );

  function submitCreate() {
    const code = (form.code || '').trim().toUpperCase();
    const discountValue = form.discountValue ? parseFloat(form.discountValue) : null;
    if (!code) {
      setMessage({ text: 'Code is required.', success: false });
      return;
    }
    if (!form.discountType || (discountValue === null || isNaN(discountValue) || discountValue < 0)) {
      setMessage({ text: 'Discount type and a valid discount value are required.', success: false });
      return;
    }
    if (!form.campaign_id) {
      setMessage({ text: 'Please select which season this promo code applies to.', success: false });
      return;
    }
    if (!form.season_id) {
      setMessage({ text: 'Please select which phase this promo code applies to.', success: false });
      return;
    }
    setMessage({ text: '', success: false });
    const validUntil = form.validUntil ? new Date(form.validUntil).toISOString() : null;
    createMutation.mutate({
      code,
      description: (form.description || '').trim() || undefined,
      campaign_id: form.campaign_id,
      event_tournament_id: form.campaign_id,
      season_id: form.season_id,
      discountType: form.discountType,
      discountValue,
      maxUses: form.maxUses ? parseInt(form.maxUses, 10) : null,
      validUntil,
      is_active: form.is_active,
    });
  }

  function openEdit(promo) {
    setEditingId(promo.id);
    setEditForm({
      description: promo.description || '',
      campaign_id: promo.event_tournament_id || '',
      season_id: promo.season_id || '',
      discountType: promo.discount_type || 'percentage',
      discountValue: promo.discount_value != null ? String(promo.discount_value) : '',
      maxUses: promo.max_uses != null ? String(promo.max_uses) : '',
      validUntil: toLocalDatetimeInput(promo.valid_until),
      is_active: promo.is_active !== false,
    });
    setMessage({ text: '', success: false });
  }

  function submitEdit() {
    if (!editingId) return;
    const discountValue = editForm.discountValue ? parseFloat(editForm.discountValue) : null;
    if (editForm.discountType && (discountValue === null || isNaN(discountValue) || discountValue < 0)) {
      setMessage({ text: 'Discount value must be a valid number.', success: false });
      return;
    }
    const validUntil = editForm.validUntil ? new Date(editForm.validUntil).toISOString() : null;
    updateMutation.mutate({
      id: editingId,
      data: {
        description: (editForm.description || '').trim() || undefined,
        campaign_id: editForm.campaign_id || null,
        event_tournament_id: editForm.campaign_id || null,
        season_id: editForm.season_id || null,
        discountType: editForm.discountType,
        discountValue: discountValue,
        maxUses: editForm.maxUses ? parseInt(editForm.maxUses, 10) : null,
        validUntil,
        is_active: editForm.is_active,
      },
    });
  }

  function toggleActive(promo) {
    updateMutation.mutate({
      id: promo.id,
      data: { is_active: !promo.is_active },
    });
  }

  return (
    <Container>
      <Title>Promo codes</Title>
      <Subtitle>
        Create codes for a specific season and phase. Legacy codes without a season still work.
        Team-name discounts: (1) loyalty names from a previous completed season (once per org), and
        (2) referral names of teams already registered in this season/phase (unlimited). Each path has its own discount below.
      </Subtitle>

      {message.text && <Message $success={message.success}>{message.text}</Message>}

      <ToggleSection>
        <div style={{ flex: 1, minWidth: 200 }}>
          <ToggleLabel>Team name as referral / loyalty code</ToggleLabel>
          <ToggleHint>
            When enabled, checkout accepts team names as discounts: loyalty (prior completed season, once) and
            current-season team referrals (teams already paid in this season/phase). Only this tenant&apos;s teams
            count. Set a separate percentage or fixed CHF for each path below.
          </ToggleHint>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-end' }}>
          <CheckboxRow style={{ margin: 0 }}>
            <input
              type="checkbox"
              checked={allowTeamReferral}
              onChange={(e) => tenantSettingsMutation.mutate({ allow_team_referral_codes: e.target.checked })}
              disabled={tenantSettingsMutation.isLoading}
            />
            <span>{allowTeamReferral ? 'Enabled' : 'Disabled'}</span>
          </CheckboxRow>
          {allowTeamReferral && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600, minWidth: 140, textAlign: 'right' }}>
                  Loyalty discount
                </span>
                <Select
                  value={loyaltyDiscountType}
                  onChange={(e) => tenantSettingsMutation.mutate({ loyalty_discount_type: e.target.value })}
                  disabled={tenantSettingsMutation.isLoading}
                  style={{ width: 'auto', minWidth: 120 }}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed amount</option>
                </Select>
                <Input
                  type="number"
                  step={loyaltyDiscountType === 'percentage' ? 1 : 0.01}
                  min="0"
                  value={localLoyaltyValue}
                  onChange={(e) => setLocalLoyaltyValue(e.target.value)}
                  onBlur={() => {
                    const v = parseFloat(localLoyaltyValue);
                    if (!isNaN(v) && v >= 0 && v !== serverLoyaltyValue) {
                      tenantSettingsMutation.mutate({ loyalty_discount_value: v });
                    }
                  }}
                  disabled={tenantSettingsMutation.isLoading}
                  style={{ width: 80 }}
                  placeholder={loyaltyDiscountType === 'percentage' ? '20' : '10'}
                />
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {loyaltyDiscountType === 'percentage' ? '%' : 'CHF'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600, minWidth: 140, textAlign: 'right' }}>
                  Team referral discount
                </span>
                <Select
                  value={teamRefDiscountType}
                  onChange={(e) => tenantSettingsMutation.mutate({ team_referral_discount_type: e.target.value })}
                  disabled={tenantSettingsMutation.isLoading}
                  style={{ width: 'auto', minWidth: 120 }}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed amount</option>
                </Select>
                <Input
                  type="number"
                  step={teamRefDiscountType === 'percentage' ? 1 : 0.01}
                  min="0"
                  value={localTeamRefValue}
                  onChange={(e) => setLocalTeamRefValue(e.target.value)}
                  onBlur={() => {
                    const v = parseFloat(localTeamRefValue);
                    if (!isNaN(v) && v >= 0 && v !== serverTeamRefValue) {
                      tenantSettingsMutation.mutate({ team_referral_discount_value: v });
                    }
                  }}
                  disabled={tenantSettingsMutation.isLoading}
                  style={{ width: 80 }}
                  placeholder={teamRefDiscountType === 'percentage' ? '20' : '10'}
                />
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {teamRefDiscountType === 'percentage' ? '%' : 'CHF'}
                </span>
              </div>
            </div>
          )}
        </div>
      </ToggleSection>

      <FormSection>
        <SectionHeading>Create new promo code</SectionHeading>
        <FormGrid>
          <FormGroup>
            <Label>Season *</Label>
            <Select
              value={form.campaign_id}
              onChange={(e) =>
                setForm({ ...form, campaign_id: e.target.value, season_id: '' })
              }
              required
            >
              <option value="">Select season</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title || c.name || c.id}
                  {c.status ? ` (${c.status})` : ''}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Phase *</Label>
            <Select
              value={form.season_id}
              onChange={(e) => setForm({ ...form, season_id: e.target.value })}
              required
              disabled={!form.campaign_id || linkedSeasonsLoading}
            >
              <option value="">
                {!form.campaign_id
                  ? 'Select a season first'
                  : linkedSeasonsLoading
                    ? 'Loading phases…'
                    : 'Select phase'}
              </option>
              {linkedSeasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.status === 'draft' ? '(draft)' : ''}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Code *</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. EARLY50"
              style={{ textTransform: 'uppercase' }}
            />
          </FormGroup>
          <FormGroup>
            <Label>Description (optional)</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. 50% off early bird"
            />
          </FormGroup>
          <FormGroup>
            <Label>Discount type *</Label>
            <Select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed amount</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Discount value *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.discountValue}
              onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
              placeholder={form.discountType === 'percentage' ? 'e.g. 25' : 'e.g. 10'}
            />
          </FormGroup>
          <FormGroup>
            <Label>Max uses (leave empty for unlimited)</Label>
            <Input
              type="number"
              min="1"
              value={form.maxUses}
              onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
              placeholder="Unlimited"
            />
          </FormGroup>
          <FormGroup>
            <Label>Valid until (optional)</Label>
            <Input
              type="datetime-local"
              value={form.validUntil}
              onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
            />
          </FormGroup>
          <FormGroup className="fullWidth">
            <CheckboxRow>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <span>Active (can be used at checkout)</span>
            </CheckboxRow>
          </FormGroup>
        </FormGrid>
        <PrimaryButton onClick={submitCreate} disabled={createMutation.isLoading}>
          {createMutation.isLoading ? 'Creating...' : 'Create promo code'}
        </PrimaryButton>
      </FormSection>

      <SectionHeading>All promo codes</SectionHeading>
      {isLoading ? (
        <LoadingState>Loading promo codes...</LoadingState>
      ) : promoCodes.length === 0 ? (
        <EmptyState>No promo codes yet. Create one above.</EmptyState>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Season</th>
                <th>Phase</th>
                <th>Description</th>
                <th>Discount</th>
                <th>Uses</th>
                <th>Valid until</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.map((promo) => (
                <React.Fragment key={promo.id}>
                  <tr>
                    <td><strong>{promo.code}</strong></td>
                    <td>
                      {promo.campaign_title ||
                        (promo.event_tournament_id ? '—' : 'Any (legacy)')}
                    </td>
                    <td>{promo.season_name || (promo.season_id ? '—' : 'Any (legacy)')}</td>
                    <td>{promo.description || '—'}</td>
                    <td>
                      {promo.discount_type === 'percentage'
                        ? `${Number(promo.discount_value)}%`
                        : `Fixed ${Number(promo.discount_value)}`}
                    </td>
                    <td>
                      {promo.current_uses ?? 0}
                      {promo.max_uses != null ? ` / ${promo.max_uses}` : ' / ∞'}
                    </td>
                    <td>{formatDateTime(promo.valid_until)}</td>
                    <td>
                      <StatusBadge $active={promo.is_active !== false}>
                        {promo.is_active !== false ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </td>
                    <td>
                      <ToggleBtn
                        $active={promo.is_active !== false}
                        onClick={() => toggleActive(promo)}
                        disabled={updateMutation.isLoading}
                      >
                        {promo.is_active !== false ? 'Disable' : 'Enable'}
                      </ToggleBtn>
                      <EditBtn onClick={() => openEdit(promo)} disabled={updateMutation.isLoading}>
                        Edit
                      </EditBtn>
                    </td>
                  </tr>
                  {editingId === promo.id && (
                    <tr>
                      <td colSpan={9} style={{ background: '#f8fafc', padding: '1rem', verticalAlign: 'top' }}>
                        <div style={{ maxWidth: 560 }}>
                          <SectionHeading style={{ marginBottom: '0.75rem' }}>Edit: {promo.code}</SectionHeading>
                          <FormGrid>
                            <FormGroup>
                              <Label>Season</Label>
                              <Select
                                value={editForm.campaign_id}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    campaign_id: e.target.value,
                                    season_id: '',
                                  })
                                }
                              >
                                <option value="">— legacy (any season)</option>
                                {campaigns.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.title || c.name || c.id}
                                  </option>
                                ))}
                              </Select>
                            </FormGroup>
                            <FormGroup>
                              <Label>Phase</Label>
                              <Select
                                value={editForm.season_id}
                                onChange={(e) => setEditForm({ ...editForm, season_id: e.target.value })}
                                disabled={!!editForm.campaign_id && editLinkedSeasons.length === 0}
                              >
                                <option value="">— legacy (any phase)</option>
                                {(editForm.campaign_id ? editLinkedSeasons : []).map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name} {s.status === 'draft' ? '(draft)' : ''}
                                  </option>
                                ))}
                              </Select>
                            </FormGroup>
                            <FormGroup className="fullWidth">
                              <Label>Description</Label>
                              <Input
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                placeholder="Optional description"
                              />
                            </FormGroup>
                            <FormGroup>
                              <Label>Discount type</Label>
                              <Select
                                value={editForm.discountType}
                                onChange={(e) => setEditForm({ ...editForm, discountType: e.target.value })}
                              >
                                <option value="percentage">Percentage</option>
                                <option value="fixed">Fixed amount</option>
                              </Select>
                            </FormGroup>
                            <FormGroup>
                              <Label>Discount value</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editForm.discountValue}
                                onChange={(e) => setEditForm({ ...editForm, discountValue: e.target.value })}
                              />
                            </FormGroup>
                            <FormGroup>
                              <Label>Max uses (empty = unlimited)</Label>
                              <Input
                                type="number"
                                min="1"
                                value={editForm.maxUses}
                                onChange={(e) => setEditForm({ ...editForm, maxUses: e.target.value })}
                                placeholder="Unlimited"
                              />
                            </FormGroup>
                            <FormGroup>
                              <Label>Valid until</Label>
                              <Input
                                type="datetime-local"
                                value={editForm.validUntil}
                                onChange={(e) => setEditForm({ ...editForm, validUntil: e.target.value })}
                              />
                            </FormGroup>
                            <FormGroup className="fullWidth">
                              <CheckboxRow>
                                <input
                                  type="checkbox"
                                  checked={editForm.is_active}
                                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                />
                                <span>Active (can be used at checkout). You can disable at any time.</span>
                              </CheckboxRow>
                            </FormGroup>
                          </FormGrid>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <PrimaryButton onClick={submitEdit} disabled={updateMutation.isLoading}>
                              {updateMutation.isLoading ? 'Saving...' : 'Save changes'}
                            </PrimaryButton>
                            <SecondaryButton onClick={() => setEditingId(null)}>Cancel</SecondaryButton>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Container>
  );
}
